import { Box, Collapse, Flex, FormControl, FormLabel, useDisclosure } from "@chakra-ui/react";
import { ReactNode } from "react";
import { useCallback, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useMap } from "react-map-gl/maplibre";

import { DecimalInput } from "@nens/chakra/components";
import { ButtonWrapper, RanaButton, SettingsIcon } from "@nens/ranalib";

import { RanaLegend } from "@/lib/rana-legend";
import { useFileDescriptorViewQuery } from "@/queries/query-file-descriptors";
import { useWmsLegendQuery } from "@/queries/query-wms-legend";

import { PublicationRasterDetailsProps } from "../types/rana-raster-types";

import { RanaRasterOpacitySlider } from "./rana-raster-opacity-slider";

// FUTURE this whole component is getting very messy with the COG rasters. We should rewrite this entire feature lib to support both COG and WMS rasters in a more generic way. For now, we just add the COG raster support here, but this is not a good long-term solution.
// https://github.com/nens/rana/issues/5285

function useColorRange(styles?: string, defaultStyles?: string) {
  const [editedColorRange, setEditedColorRange] = useState<[number, number] | undefined>();

  const [, styleMin, styleMax] = styles?.split(":") ?? defaultStyles?.split(":") ?? [];
  const defaultRange: [number, number] = [+styleMin, +styleMax];

  const [min, max] = editedColorRange ?? defaultRange;

  const stopEdit = () => setEditedColorRange(undefined);

  const hasEditedColorRange =
    editedColorRange !== undefined && (min !== +styleMin || max !== +styleMax);

  return {
    min,
    max,
    editedColorRange,
    setEditedColorRange,
    stopEdit,
    hasEditedColorRange,
  };
}

function useEditableColorRange(defaultRange: [number, number] | undefined) {
  const [editedColorRange, setEditedColorRange] = useState<[number, number] | undefined>();
  const [min, max] = editedColorRange ?? defaultRange ?? [0, 0];

  const hasEditedColorRange =
    editedColorRange !== undefined &&
    (!defaultRange || min !== defaultRange[0] || max !== defaultRange[1]);

  return {
    min,
    max,
    setEditedColorRange,
    stopEdit: () => setEditedColorRange(undefined),
    hasEditedColorRange,
  };
}

type RasterColorSettingsBaseProps = {
  label: PublicationRasterDetailsProps["label"];
  legend: ReactNode;
  opacity: number;
  setOpacity: (opacity: number) => void;
  showOpacity?: boolean;
  decimalPlaces?: number;
  min: number;
  max: number;
  setEditedColorRange: (range: [number, number]) => void;
  applyColorRange: () => void;
  hasEditedColorRange: boolean;
  onScaleToViewport: () => void;
  onResetScale?: () => void;
  isDiscrete?: boolean;
  children?: React.ReactNode;
};

function RasterColorSettingsBase({
  legend,
  opacity,
  setOpacity,
  min,
  max,
  setEditedColorRange,
  applyColorRange,
  hasEditedColorRange,
  onScaleToViewport,
  onResetScale,
  isDiscrete,
  children,
  showOpacity = true,
  decimalPlaces = 1,
}: RasterColorSettingsBaseProps) {
  const { isOpen, onToggle } = useDisclosure();

  return (
    <Box pt={1} pb={2}>
      <Flex alignItems="stretch" justifyContent="space-between" gap={2}>
        {legend}
        <Flex alignItems={isDiscrete ? "flex-start" : "center"}>
          <RanaButton
            onClick={onToggle}
            bg="transparent"
            h="fit"
            w="fit"
            p={2}
            mt={1}
            color="brand.analytic"
            justifySelf="center"
            aria-label="Toggle raster settings"
          >
            <SettingsIcon boxSize={6} />
          </RanaButton>
        </Flex>
      </Flex>
      <Collapse in={isOpen} unmountOnExit>
        <ButtonWrapper mt={4} flexDirection="column">
          {showOpacity ? (
            <RanaRasterOpacitySlider opacity={opacity} setOpacity={setOpacity} />
          ) : null}

          <Collapse in={!isDiscrete} unmountOnExit>
            <ButtonWrapper flexDirection="column">
              <Flex gap={2} alignItems="flex-end">
                <FormControl flex={1} minW={0}>
                  <FormLabel>Min</FormLabel>
                  <DecimalInput
                    bg="brand.grey.100"
                    value={+min.toFixed(decimalPlaces)}
                    onChange={(value) => setEditedColorRange([value, max])}
                    zIndex="base"
                    minW={0}
                  />
                </FormControl>

                <FormControl flex={1} minW={0}>
                  <FormLabel>Max</FormLabel>
                  <DecimalInput
                    bg="brand.grey.100"
                    value={+max.toFixed(decimalPlaces)}
                    onChange={(value) => setEditedColorRange([min, value])}
                    zIndex="base"
                    minW={0}
                  />
                </FormControl>

                <RanaButton
                  onClick={applyColorRange}
                  variant="primary"
                  isDisabled={!hasEditedColorRange}
                  flexShrink={0}
                >
                  <FormattedMessage id="button.apply" defaultMessage="Apply" />
                </RanaButton>
              </Flex>

              <RanaButton onClick={onScaleToViewport} variant="secondary">
                <FormattedMessage id="label.scale_viewport" defaultMessage="Scale to viewport" />
              </RanaButton>

              {onResetScale && (
                <RanaButton onClick={onResetScale} variant="secondary">
                  <FormattedMessage id="label.scale_reset" defaultMessage="Reset scale" />
                </RanaButton>
              )}
            </ButtonWrapper>
          </Collapse>

          {children}
        </ButtonWrapper>
      </Collapse>
    </Box>
  );
}

export const RanaRasterColorSettings = ({
  label,
  descriptor,
  styles,
  layer,
  opacity = 1,
  setOpacity,
  setColorRange,
  setColorBounds,
  setDefaultColor,
  unit,
  children,
}: PublicationRasterDetailsProps) => {
  const { current: map } = useMap();

  const updateColorBounds = useCallback(() => {
    if (!map) return;
    const [[minX, minY], [maxX, maxY]] = map.getBounds().toArray();
    setColorBounds([minX, minY, maxX, maxY]);
  }, [map, setColorBounds]);

  const { data: defaultRaster } = useFileDescriptorViewQuery(descriptor.id, "lizard-raster");

  const legendResponse = useWmsLegendQuery(descriptor.id, layer, styles);

  const defaultStyles = defaultRaster?.options.styles;

  const { min, max, setEditedColorRange, stopEdit, hasEditedColorRange } = useColorRange(
    styles,
    defaultStyles
  );

  if (!defaultRaster) return null;

  const hasCustomStyle = styles !== defaultRaster.options.styles;

  const colormapType = legendResponse.data?.colormap_type;
  const isDiscrete = colormapType === "discrete";

  return (
    <RasterColorSettingsBase
      label={label}
      legend={
        <RanaLegend
          label={label}
          descriptorId={descriptor.id}
          layer={layer}
          styles={styles}
          unit={unit}
          flexGrow={1}
        />
      }
      opacity={opacity}
      setOpacity={setOpacity}
      min={min}
      max={max}
      setEditedColorRange={setEditedColorRange}
      applyColorRange={() => {
        setColorRange([min, max]);
        stopEdit();
      }}
      hasEditedColorRange={hasEditedColorRange}
      onScaleToViewport={() => {
        updateColorBounds();
        stopEdit();
      }}
      onResetScale={
        hasCustomStyle
          ? () => {
              setDefaultColor();
              stopEdit();
            }
          : undefined
      }
      isDiscrete={isDiscrete}
    >
      {children}
    </RasterColorSettingsBase>
  );
};

type RanaCogRasterColorSettingsProps = {
  label: string;
  legendId: string;
  legend: { value: number; color: string }[];
  unit?: string;
  colorRange: [number, number] | undefined;
  defaultColorRange: [number, number] | undefined;
  setColorRange: (range: [number, number]) => void;
  setColorBounds: (bounds: [number, number, number, number]) => void;
  setDefaultColor: () => void;
};

export function RanaCogRasterColorSettings({
  label,
  legendId,
  legend,
  unit,
  colorRange,
  defaultColorRange,
  setColorRange,
  setColorBounds,
  setDefaultColor,
}: RanaCogRasterColorSettingsProps) {
  const { current: map } = useMap();

  const { min, max, setEditedColorRange, hasEditedColorRange, stopEdit } =
    useEditableColorRange(colorRange);

  const canReset =
    !!colorRange &&
    !!defaultColorRange &&
    (colorRange[0] !== defaultColorRange[0] || colorRange[1] !== defaultColorRange[1]);

  if (!colorRange || !legend.length) return null;

  const applyNormalizedRange = () => {
    const sorted: [number, number] = min <= max ? [min, max] : [max, min];
    const span = sorted[1] - sorted[0];
    const safeRange: [number, number] =
      span > 0
        ? sorted
        : [sorted[0] - Math.max(Math.abs(sorted[0]) * 0.01, 1e-6), sorted[1] + 1e-6];

    setColorRange(safeRange);
    stopEdit();
  };

  return (
    <RasterColorSettingsBase
      label={label}
      legend={
        <RanaLegend.FromStops
          label={label}
          legend={legend}
          legendId={legendId}
          unit={unit}
          flexGrow={1}
        />
      }
      opacity={1}
      setOpacity={() => undefined}
      showOpacity={false}
      min={min}
      max={max}
      setEditedColorRange={setEditedColorRange}
      applyColorRange={applyNormalizedRange}
      hasEditedColorRange={hasEditedColorRange}
      decimalPlaces={3}
      onScaleToViewport={() => {
        if (!map) return;
        const [[minX, minY], [maxX, maxY]] = map.getBounds().toArray();
        setColorBounds([minX, minY, maxX, maxY]);
        stopEdit();
      }}
      onResetScale={
        canReset
          ? () => {
              setDefaultColor();
              stopEdit();
            }
          : undefined
      }
    />
  );
}
